import React, { useRef, useState, useEffect } from 'react';
import { X, Check, RotateCcw, Camera, Upload, AlertCircle, User, FileText, CheckCircle2 } from 'lucide-react';
import { OrdemDeServico } from '../../types/ordemServico';
import { ComprovanteEntregaData } from '../../types/areaMotorista';

interface AreaMotoristaAssinaturaModalProps {
  isOpen: boolean;
  onClose: () => void;
  os: OrdemDeServico;
  onSalvar: (comprovante: ComprovanteEntregaData) => Promise<boolean>;
  isSaving: boolean;
}

export const AreaMotoristaAssinaturaModal: React.FC<AreaMotoristaAssinaturaModalProps> = ({
  isOpen,
  onClose,
  os,
  onSalvar,
  isSaving
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  const [nomeRecebedor, setNomeRecebedor] = useState('');
  const [docRecebedor, setDocRecebedor] = useState('');
  const [cargoParentesco, setCargoParentesco] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNomeRecebedor('');
      setDocRecebedor('');
      setCargoParentesco('');
      setObservacoes('');
      setFotoPreview(null);
      setFormError(null);
      setSucesso(false);
      setHasSignature(false);

      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const parentWidth = canvas.parentElement?.clientWidth || 360;
          canvas.width = parentWidth;
          canvas.height = 150;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#0f172a';
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
          }
        }
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Touch & Mouse Canvas Helpers
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const limparAssinatura = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleFotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!nomeRecebedor.trim()) {
      setFormError('Informe o nome de quem está recebendo a entrega.');
      return;
    }

    if (!docRecebedor.trim()) {
      setFormError('Informe o RG ou CPF do recebedor.');
      return;
    }

    if (!hasSignature) {
      setFormError('Por favor, solicite a assinatura na tela.');
      return;
    }

    const canvas = canvasRef.current;
    const assinaturaBase64 = canvas ? canvas.toDataURL('image/png') : '';

    const payload: ComprovanteEntregaData = {
      os_id: os.id,
      nome_recebedor: nomeRecebedor.trim(),
      documento_recebedor: docRecebedor.trim(),
      grau_parentesco_cargo: cargoParentesco.trim() || undefined,
      assinatura_base64: assinaturaBase64,
      fotos_comprovante: fotoPreview ? [fotoPreview] : [],
      observacoes: observacoes.trim() || undefined,
      data_hora_finalizacao: new Date().toISOString()
    };

    const ok = await onSalvar(payload);
    if (ok) {
      setSucesso(true);
      setTimeout(() => {
        onClose();
      }, 1200);
    } else {
      setFormError('Erro ao finalizar a OS. Tente novamente.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-auto max-h-[92vh] flex flex-col">
        
        {/* Header */}
        <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Comprovante de Entrega Digital</h2>
              <p className="text-xs text-slate-400">OS #{os.id} • Finalização de Viagem</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {sucesso ? (
          <div className="p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto animate-bounce">
              <Check className="w-8 h-8 stroke-[3]" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Entrega Concluída com Sucesso!</h3>
            <p className="text-sm text-slate-600">O comprovante e a assinatura foram salvos na Ordem de Serviço #{os.id}.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 text-left flex-1">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            {/* Info Resumida da OS */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
              <p className="font-semibold text-slate-800 line-clamp-1">📍 Destino: {os.entrega || 'Endereço de Entrega'}</p>
              <p className="text-slate-500">Carga: {os.descricao || 'Carga Geral'}</p>
            </div>

            {/* Dados do Recebedor */}
            <div className="space-y-3">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <User className="w-4 h-4 text-slate-500" />
                Dados de Quem Recebeu a Mercadoria
              </label>
              
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Nome Completo do Recebedor <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Carlos Eduardo de Oliveira"
                  value={nomeRecebedor}
                  onChange={(e) => setNomeRecebedor(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-xl border border-slate-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    RG ou CPF <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: 48.987.654-X"
                    value={docRecebedor}
                    onChange={(e) => setDocRecebedor(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-slate-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    Cargo / Parentesco
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Gerente / Portaria"
                    value={cargoParentesco}
                    onChange={(e) => setCargoParentesco(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-slate-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Canvas de Assinatura */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-slate-500" />
                  Assinatura na Tela <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={limparAssinatura}
                  className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1 font-medium transition"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Limpar
                </button>
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 relative overflow-hidden touch-none">
                <canvas
                  ref={canvasRef}
                  width={440}
                  height={150}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full h-[140px] cursor-crosshair block"
                />
                {!hasSignature && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-slate-400 text-xs font-medium">
                    ✍️ O cliente deve assinar com o dedo aqui
                  </div>
                )}
              </div>
            </div>

            {/* Foto do Comprovante / Canhoto */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <Camera className="w-4 h-4 text-slate-500" />
                Foto do Canhoto ou Carga (Opcional)
              </label>

              {fotoPreview ? (
                <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-slate-100 max-h-36 flex items-center justify-center">
                  <img src={fotoPreview} alt="Comprovante" className="max-h-36 w-auto object-contain" />
                  <button
                    type="button"
                    onClick={() => setFotoPreview(null)}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-lg hover:bg-black"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="border-2 border-dashed border-slate-300 rounded-xl p-3 bg-slate-50 hover:bg-slate-100 cursor-pointer flex items-center justify-center gap-2 text-xs font-medium text-slate-600 transition">
                  <Upload className="w-4 h-4 text-slate-500" />
                  <span>Tirar foto ou anexar canhoto</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFotoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Observações */}
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">
                Observações de Entrega (Opcional)
              </label>
              <textarea
                rows={2}
                placeholder="Ex: Entregue na doca 2 sem avarias..."
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:ring-2 focus:ring-red-500 outline-none resize-none"
              />
            </div>

            {/* Footer Buttons */}
            <div className="pt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="flex-1 py-3 px-4 rounded-xl border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-100 transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex-1 py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 transition disabled:opacity-50"
              >
                {isSaving ? (
                  <span>Salvando...</span>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Concluir Entrega</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
